<template>
  <div class="login-page" @keyup.enter="login">
    <div class="ambient ambient-left" aria-hidden="true"></div>
    <div class="ambient ambient-right" aria-hidden="true"></div>

    <main class="login-shell">
      <section class="login-panel" aria-label="账号登录">
        <div class="login-card">
          <header class="login-header">
            <div class="system-status">
              <span class="status-dot" aria-hidden="true"></span>
              数据平台服务
            </div>
            <h2>欢迎回来</h2>
            <p>请使用数据网站账号登录</p>
          </header>

          <el-form
            ref="loginForm"
            class="login-form"
            :model="loginForm"
            :rules="rules"
            label-position="top"
            hide-required-asterisk
          >
            <el-form-item label="账号" prop="account">
              <el-input
                v-model="loginForm.account"
                name="username"
                autocomplete="username"
                placeholder="请输入账号"
              />
            </el-form-item>

            <el-form-item label="密码" prop="password">
              <el-input
                v-model="loginForm.password"
                name="password"
                type="password"
                autocomplete="current-password"
                placeholder="请输入密码"
                show-password
              />
            </el-form-item>

            <el-form-item
              v-if="captcha.enabled"
              label="验证码"
              prop="captchaAnswer"
            >
              <div class="captcha-row">
                <el-input
                  v-model="loginForm.captchaAnswer"
                  autocomplete="off"
                  placeholder="请输入计算结果"
                />
                <button
                  v-if="captcha.image"
                  class="captcha-button"
                  type="button"
                  title="点击刷新验证码"
                  @click="refreshCaptcha"
                >
                  <img
                    :src="captcha.image"
                    alt="登录验证码，点击刷新"
                    class="captcha-image"
                  />
                </button>
                <el-button v-else class="captcha-reload" type="text" @click="refreshCaptcha">
                  重新获取
                </el-button>
              </div>
              <p class="captcha-tip">看不清？点击验证码即可刷新</p>
            </el-form-item>

            <el-form-item class="server-form-item" label="登录环境" required>
              <el-radio-group
                v-model="selectedServer"
                class="server-options"
                @change="handleServerChange"
              >
                <el-radio-button label="northwest">服务器 1</el-radio-button>
                <el-radio-button label="main">服务器 2</el-radio-button>
                <el-radio-button label="dev">本地</el-radio-button>
              </el-radio-group>
              <div class="server-hint">
                <span aria-hidden="true"></span>
                当前连接：{{ currentServerName }}
              </div>
            </el-form-item>

            <div class="login-options">
              <el-checkbox v-model="checked" @change="saveUser">
                记住密码
              </el-checkbox>
              <span class="security-note">加密保存在当前浏览器</span>
            </div>

            <el-button
              class="login-button"
              type="primary"
              :loading="loginLoading"
              :disabled="loginLoading"
              @click="login"
            >
              进入数据平台
            </el-button>
          </el-form>
        </div>
      </section>
    </main>

    <footer class="login-footer">
      <span>© 超新芯科技有限公司</span>
      <span class="footer-divider" aria-hidden="true"></span>
      <a href="https://beian.miit.gov.cn" target="_blank" rel="noopener noreferrer">
        {{ icpText }}
      </a>
    </footer>
  </div>
</template>

<script>
// 引入 base 模块（管理服务器配置）
import base from "@/api/base";
import { getIcpText } from "@/Utils/siteInfo";
import {
  loadRememberedAccount,
  migrateLegacyLoginStorage,
  saveRememberedAccount,
} from "@/Utils/loginStorage";
import {
  loadBrowserCredential,
  preventSilentCredentialAccess,
  storeBrowserCredential,
} from "@/Utils/browserCredentials";

// 引入登录 API
import Auth from "@/api/web/User";

export default {
  name: "MyLogin",
  data() {
    return {
      // 默认选中上次保存的服务器，否则为服务器1
      selectedServer: "northwest",

      loginForm: {
        account: "",
        password: "",
        captchaAnswer: "",
      },
      rules: {
        account: [
          { required: true, message: "请输入用户名", trigger: "blur" },
          { min: 1, max: 50, message: "长度 1-50 字符", trigger: "blur" },
        ],
        password: [
          { required: true, message: "请输入密码", trigger: "blur" },
          { min: 3, max: 72, message: "长度 3-72 字符", trigger: "blur" },
        ],
      },
      checked: false,
      loginLoading: false,
      captcha: {
        enabled: false,
        captchaId: "",
        image: "",
        expiresInSeconds: 0,
      },
    };
  },
  computed: {
    icpText() {
      return getIcpText();
    },
    currentServerName() {
      const server = base.SERVER_OPTIONS[this.selectedServer];
      return server ? server.name : "未选择";
    },
  },
  methods: {
    // 切换服务器
    handleServerChange(key) {
      base.setServer(key); // 更新 base 中的 currentServer 并存 localStorage
      this.$message({
        message: `已切换到：${base.currentServer.name}`,
        type: "success",
        offset: 100,
      });
      this.loginForm.captchaAnswer = "";
      this.refreshCaptcha();
    },

    async refreshCaptcha() {
      const res = await Auth.getCaptcha();
      if (res.error) {
        this.captcha = {
          enabled: true,
          captchaId: "",
          image: "",
          expiresInSeconds: 0,
        };
        this.$message.error(res.error);
        return;
      }
      this.captcha = res.data || {
        enabled: false,
        captchaId: "",
        image: "",
        expiresInSeconds: 0,
      };
      this.loginForm.captchaAnswer = "";
    },

    // 登录方法
    async login() {
      if (this.loginLoading) {
        return;
      }
      const valid = await new Promise((resolve) => {
        this.$refs.loginForm.validate((result) => resolve(result));
      });
      if (!valid) {
        return;
      }
      if (this.captcha.enabled && !this.loginForm.captchaAnswer.trim()) {
        this.$message.error("请输入验证码计算结果");
        return;
      }

      this.loginLoading = true;
      try {
        const res = await Auth.login({
          account: this.loginForm.account.trim(),
          password: this.loginForm.password,
          captchaId: this.captcha.enabled ? this.captcha.captchaId : null,
          captchaAnswer: this.captcha.enabled
            ? this.loginForm.captchaAnswer.trim()
            : null,
        });

        if (res.error) {
          this.$message.error(res.error);
          return;
        }

        await this.$store.dispatch("loginSucceeded", res.data);
        saveRememberedAccount(
          localStorage,
          this.checked,
          this.loginForm.account.trim()
        );
        if (this.checked) {
          await storeBrowserCredential(
            this.loginForm.account.trim(),
            this.loginForm.password
          );
        } else {
          await preventSilentCredentialAccess();
        }
        try {
          await this.$store.dispatch("initializeAuth", { force: true });
        } catch (error) {
          this.$message.error(
            error.status === 503
              ? "登录成功，但权限服务暂不可用，请稍后重试"
              : "登录状态初始化失败，请重新登录"
          );
          return;
        }

        this.$router.push({ name: "NavBar" });

        this.$message({
          message: "登录成功",
          type: "success",
          offset: 100,
        });
      } catch (err) {
        this.$message.error(err.message || "网络错误，请检查服务器是否可达");
      } finally {
        this.loginLoading = false;
        this.loginForm.captchaAnswer = "";
        await this.refreshCaptcha();
      }
    },

    // 只保存记忆偏好；密码由浏览器密码管理器或加密凭据库持久化
    async saveUser() {
      saveRememberedAccount(
        localStorage,
        this.checked,
        this.loginForm.account.trim()
      );
      if (!this.checked) {
        await preventSilentCredentialAccess();
      }
    },

    // 检查版本（可选）
    async checkVersion() {
      const lastReloadTime =
        Number(localStorage.getItem("lastReloadTime")) || 0;
      const currentVersion = localStorage.getItem("website_version");
      const now = Date.now();

      try {
        const res = await Auth.getVersion();
        const newVersion = res.data;

        if (newVersion !== currentVersion && now - lastReloadTime > 3600000) {
          localStorage.setItem("lastReloadTime", now.toString());
          localStorage.setItem("website_version", newVersion);
          window.location.reload();
        }
        document.title = `数据网站 V${newVersion}`;
      } catch (err) {
        console.error("版本检查失败:", err);
      }
    },
  },
  mounted() {
    // 2. 恢复上次选择的服务器
    const saved = localStorage.getItem("selectedServer");
    this.selectedServer = base.SERVER_OPTIONS[saved] ? saved : "northwest"; // 同步到 radio 显示
    base.setServer(this.selectedServer); // 确保 base 使用正确地址

    // 3. 迁移旧“记住密码”数据，只保留用户曾明确选择记住的账号
    migrateLegacyLoginStorage(localStorage);
    const remembered = loadRememberedAccount(localStorage);
    this.checked = remembered.remember;
    this.loginForm.account = remembered.account;
    loadBrowserCredential(this.checked, remembered.account).then((credential) => {
      if (!credential) return;
      this.loginForm.account = credential.account;
      this.loginForm.password = credential.password;
    });

    // 5. 清空会话
    sessionStorage.clear();

    // 6. 检查版本
    this.checkVersion();
    this.refreshCaptcha();
  },
};
</script>

<style lang="less" scoped>
.login-page {
  --primary: #18b89d;
  --primary-dark: #0f8d7a;
  --text-main: #12253f;
  --text-secondary: #637188;
  position: relative;
  min-height: 100vh;
  overflow-x: hidden;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  box-sizing: border-box;
  padding: 56px 48px 76px;
  color: var(--text-main);
  background:
    radial-gradient(circle at 18% 20%, rgba(35, 201, 178, 0.24), transparent 32%),
    radial-gradient(circle at 82% 75%, rgba(43, 119, 226, 0.22), transparent 34%),
    linear-gradient(135deg, #071a2f 0%, #0b2943 48%, #0b3550 100%);
}

.login-page,
.login-page * {
  box-sizing: border-box;
}

.login-page::before {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: 0.18;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.08) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.08) 1px, transparent 1px);
  background-size: 64px 64px;
  mask-image: linear-gradient(to right, #000, transparent 72%);
}

.ambient {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  filter: blur(1px);
}

.ambient-left {
  width: 420px;
  height: 420px;
  left: -210px;
  bottom: -170px;
  border: 1px solid rgba(70, 226, 202, 0.22);
  box-shadow:
    0 0 0 60px rgba(70, 226, 202, 0.035),
    0 0 0 120px rgba(70, 226, 202, 0.025);
}

.ambient-right {
  width: 320px;
  height: 320px;
  right: -130px;
  top: -120px;
  background: rgba(27, 163, 220, 0.08);
  box-shadow: 0 0 90px rgba(27, 163, 220, 0.18);
}

.login-shell {
  position: relative;
  z-index: 1;
  width: min(470px, 100%);
  margin: 0 auto;
  display: flex;
  justify-content: center;
  align-items: center;
}

.login-panel {
  width: 100%;
}

.login-card {
  width: 100%;
  padding: 42px 42px 36px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.98);
  border: 1px solid rgba(255, 255, 255, 0.72);
  box-shadow:
    0 32px 90px rgba(0, 12, 30, 0.32),
    0 3px 12px rgba(0, 17, 36, 0.1);
}

.login-header {
  margin-bottom: 30px;
}

.system-status {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  color: #6c7b90;
  font-size: 12px;
}

.status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #1fbd92;
  box-shadow: 0 0 0 4px rgba(31, 189, 146, 0.12);
}

.login-header h2 {
  margin: 0;
  color: var(--text-main);
  font-size: 30px;
  line-height: 1.25;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.login-header p {
  margin: 10px 0 0;
  color: var(--text-secondary);
  font-size: 14px;
}

.login-form ::v-deep .el-form-item {
  margin-bottom: 20px;
}

.login-form ::v-deep .el-form-item__label {
  height: auto;
  padding: 0 0 8px;
  color: #344258;
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
}

.login-form ::v-deep .el-input__inner {
  height: 46px;
  padding: 0 14px;
  color: var(--text-main);
  border-radius: 10px;
  border-color: #dbe2ea;
  background: #f8fafc;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.login-form ::v-deep .el-input__inner:hover {
  border-color: #a9b7c8;
}

.login-form ::v-deep .el-input__inner:focus {
  border-color: var(--primary);
  background: #fff;
  box-shadow: 0 0 0 3px rgba(24, 184, 157, 0.12);
}

.captcha-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 128px;
  align-items: center;
  gap: 10px;
}

.captcha-button {
  width: 128px;
  height: 46px;
  padding: 0;
  overflow: hidden;
  cursor: pointer;
  border-radius: 10px;
  border: 1px solid #dbe2ea;
  background: #f7f9fb;
}

.captcha-button:hover {
  border-color: var(--primary);
}

.captcha-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.captcha-reload {
  width: 128px;
  height: 46px;
  border: 1px dashed #c8d2de;
  border-radius: 10px;
}

.captcha-tip {
  margin: 6px 0 0;
  color: #94a0b1;
  font-size: 11px;
  line-height: 1.4;
}

.server-form-item {
  margin-top: 2px;
}

.server-options {
  display: flex;
  width: 100%;
}

.server-options ::v-deep .el-radio-button {
  flex: 1;
}

.server-options ::v-deep .el-radio-button__inner {
  width: 100%;
  height: 40px;
  padding: 0 8px;
  color: #5e6d82;
  font-size: 12px;
  line-height: 38px;
  border-color: #dbe2ea;
  box-shadow: none;
}

.server-options ::v-deep .el-radio-button:first-child .el-radio-button__inner {
  border-radius: 9px 0 0 9px;
}

.server-options ::v-deep .el-radio-button:last-child .el-radio-button__inner {
  border-radius: 0 9px 9px 0;
}

.server-options ::v-deep .el-radio-button__orig-radio:checked + .el-radio-button__inner {
  color: #0b7768;
  border-color: #8cdbce;
  background: #eaf9f6;
  box-shadow: -1px 0 0 0 #8cdbce;
}

.server-hint {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-top: 8px;
  color: #8b98aa;
  font-size: 11px;
  line-height: 1.4;
}

.server-hint span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #1fbd92;
}

.login-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 2px 0 22px;
}

.login-options ::v-deep .el-checkbox__label {
  color: #56657a;
  font-size: 13px;
}

.login-options ::v-deep .el-checkbox__input.is-checked .el-checkbox__inner {
  border-color: var(--primary);
  background-color: var(--primary);
}

.security-note {
  color: #98a3b3;
  font-size: 11px;
}

.login-button {
  width: 100%;
  height: 48px;
  border: 0;
  border-radius: 11px;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.05em;
  background: linear-gradient(135deg, var(--primary), var(--primary-dark));
  box-shadow: 0 12px 24px rgba(15, 141, 122, 0.2);
}

.login-button:hover,
.login-button:focus {
  background: linear-gradient(135deg, #20c4a7, #0d8271);
  box-shadow: 0 14px 28px rgba(15, 141, 122, 0.27);
}

.login-footer {
  position: absolute;
  z-index: 1;
  left: 50%;
  bottom: 24px;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: calc(100% - 40px);
  color: rgba(218, 235, 246, 0.5);
  font-size: 12px;
}

.login-footer a {
  color: inherit;
  text-decoration: none;
  transition: color 0.2s ease;
}

.login-footer a:hover {
  color: rgba(229, 244, 252, 0.84);
}

.footer-divider {
  width: 1px;
  height: 12px;
  background: rgba(218, 235, 246, 0.2);
}

@media (max-width: 1080px) {
  .login-page {
    padding-inline: 36px;
  }
}

@media (max-width: 860px) {
  .login-page {
    justify-content: flex-start;
    overflow-y: auto;
    padding: 36px 24px 78px;
  }

  .login-shell {
    max-width: 520px;
  }

  .login-card {
    padding: 38px;
  }

  .login-footer {
    position: relative;
    left: auto;
    bottom: auto;
    transform: none;
    margin-top: 34px;
  }
}

@media (max-width: 520px) {
  .login-page {
    padding: 24px 16px 30px;
  }

  .login-card {
    padding: 30px 22px 26px;
    border-radius: 20px;
  }

  .login-header {
    margin-bottom: 26px;
  }

  .login-header h2 {
    font-size: 27px;
  }

  .captcha-row {
    grid-template-columns: minmax(0, 1fr) 112px;
  }

  .captcha-button,
  .captcha-reload {
    width: 112px;
  }

  .login-options {
    align-items: flex-start;
    gap: 12px;
  }

  .security-note {
    text-align: right;
  }

  .login-footer {
    flex-wrap: wrap;
    gap: 8px;
    font-size: 11px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .login-page *,
  .login-page *::before,
  .login-page *::after {
    scroll-behavior: auto !important;
    transition-duration: 0.01ms !important;
  }
}
</style>
